// BRIEF 117 §3 — HOVER A BADGE: WHO IS INVOLVED, IN FULL.
//
// One component, mounted by every host that draws a row's badge stack. It is
// the wrapper the stack already had — the badge AREA, whose reserved width
// 116 §2 established — with a hover panel attached; hosts pass their sections
// and keep everything else (the click-to-edit, the reserved width, the dash).
//
// ── WHY NOT A NATIVE `title` ────────────────────────────────────────────────
//
// §3 says it outright: "not a native `title` — it needs structure". A `title`
// is one string with no lines, no headings and no styling, which is what 116 §2
// left the tail as. The native tooltip is therefore REMOVED from the area by
// the hosts that mount this: two tooltips over one element is worse than
// either, and the panel is a superset (it names the subtask holders too, which
// the tail never did).
//
// ── WHY A PORTAL ───────────────────────────────────────────────────────────
//
// The task row's badge area lives in a `<td>` and `tbody td { overflow: hidden }`
// (App.css:550). A panel rendered in place would be clipped by its own cell.
// This is CLAUDE.md's documented portal pattern: capture the trigger's
// `getBoundingClientRect()` on the event that opens it, render into
// `document.body` with `position: fixed` against that rect, and let it escape
// every ancestor's overflow.
//
// ── THE TWO DELAYS ─────────────────────────────────────────────────────────
//
// OPEN after 500ms (§3's number): a stack of badges sits inside a dense row and
// an instant panel would flash open on every pass of the pointer.
// CLOSE after a short GRACE, because §3 asks that the panel "can be moved into"
// — the pointer has to cross the gap between the badge and the panel, and any
// close-on-mouseout without a grace makes that impossible. Entering the panel
// cancels the pending close; leaving it starts one again.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactNode } from "react";
import type { BadgePerson } from "../lib/badgeStack";
import { RAD, FS, Z } from "../lib/tokens";

export type BadgeSection = { label: string; people: BadgePerson[] };

/** §3's number. */
const OPEN_MS = 500;
/** Long enough to cross the 6px gap below, short enough not to linger. */
const GRACE_MS = 220;
/**
 * Above rows, below modals — §3's own bound, resolved against the tree:
 * `.modal-overlay` is `z-index: 100` (App.css:940) and nothing on a row
 * declares one above 4.
 */
const PANEL_Z = Z.badgePanel;

function Panel({
  rect,
  sections,
  onEnter,
  onLeave,
}: {
  rect: DOMRect;
  sections: BadgeSection[];
  onEnter: () => void;
  onLeave: () => void;
}) {
  /* Below the badges when there is room, above them when there is not. The
     panel is small and the rows it hangs off can sit anywhere in a long list,
     so the flip is measured against the viewport rather than assumed — the
     same posture 114 §2 ruled for the Combobox menu. */
  const below = rect.bottom + 8 + 160 < window.innerHeight;
  const style: CSSProperties = {
    position: "fixed",
    left: Math.max(8, Math.min(rect.left, window.innerWidth - 248)),
    ...(below ? { top: rect.bottom + 6 } : { bottom: window.innerHeight - rect.top + 6 }),
    zIndex: PANEL_Z,
    minWidth: 180,
    maxWidth: 240,
    background: "var(--bc)",
    border: "1px solid var(--br)",
    borderRadius: RAD.r8,
    boxShadow: "0 6px 24px rgba(0,0,0,.28)",
    padding: "6px 8px",
    pointerEvents: "auto",
  };
  return createPortal(
    <div style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {sections.map((s, si) => (
        <div key={s.label} style={{ marginTop: si === 0 ? 0 : 6 }}>
          <div
            style={{
              fontSize: FS.s9,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              color: "var(--tm)",
              marginBottom: 2,
            }}
          >
            {s.label}
          </div>
          {s.people.map((p, i) => (
            <div
              key={`${p.initials}-${p.name}-${i}`}
              style={{
                fontSize: FS.s11,
                color: "var(--tp)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "16px",
              }}
            >
              <span style={{ fontWeight: 700 }}>{p.initials}</span>
              {" — "}
              {p.name}
            </div>
          ))}
        </div>
      ))}
    </div>,
    document.body,
  );
}

export default function BadgeHoverArea({
  sections,
  style,
  onClick,
  children,
  ...rest
}: {
  /** Sections with nobody in them are dropped here, so hosts may pass all of
      them unconditionally — §3: "Sections with nobody are omitted entirely." */
  sections: BadgeSection[];
  style?: CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  children?: ReactNode;
  [k: string]: unknown;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filled = sections.filter((s) => s.people.length > 0);

  const clear = () => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
    openT.current = closeT.current = null;
  };
  // A row can unmount while a timer is pending (a filter flip, a nav): the
  // callback would then setState on a dead component.
  useEffect(() => clear, []);

  const enter = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!filled.length) return;
      const el = e.currentTarget;
      clear();
      openT.current = setTimeout(() => setRect(el.getBoundingClientRect()), OPEN_MS);
    },
    [filled.length],
  );
  const leave = useCallback(() => {
    if (openT.current) clearTimeout(openT.current);
    openT.current = null;
    closeT.current = setTimeout(() => setRect(null), GRACE_MS);
  }, []);
  const holdOpen = useCallback(() => {
    if (closeT.current) clearTimeout(closeT.current);
    closeT.current = null;
  }, []);

  return (
    <span
      {...rest}
      style={style}
      onClick={onClick}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {children}
      {rect && filled.length > 0 && (
        <Panel rect={rect} sections={filled} onEnter={holdOpen} onLeave={leave} />
      )}
    </span>
  );
}
